import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  SelectChangeEvent,
  Typography,
  Autocomplete,
  CircularProgress,
  Menu,
  Chip
} from "@mui/material";
import { ArrowBack, Close, GetApp, KeyboardArrowDown } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "./MdmReportsFilter.css";
import { MdmReportsConfig, newReportConfig, defaultMdmReportsConfig } from "./mdmReportsUtils";
import MdmReportsPreview from "./OldUiComponents/OldMDMReportPreview";
import DateRangeSelector from "./NewDateFilter";
import { 
  fetchFilterValues, 
  fetchSalesDesignations, 
  fetchUsersByDesignation,
  fetchGeographicalLevels,
  fetchGeographicalLocations,
  fetchGeographicalLocationsUnder,
  fetchChildrenUsers,
  fetchLocationUsers,
  fetchAvailableFilters,
  FilterOption
} from "../../services/reportsDataService";
import { 
  handleMdmReportDownload,
  loadDistributorOptionsForHierarchy,
  collectDistributorCodes
} from "../../services/mdmReportsDownloadService";
import { 
  loadCustomFiltersForReport,
  isMergedFilterForReport,
  getMergedFilterSources
} from "../../services/mdmCustomFiltersService";
import { useSelector } from "react-redux";
import { openPopup } from "../../stateManagement/actions/popupActions";
import store from "../../stateManagement/store";
import { Loader } from "../../components/loader/Loader";

interface MdmReportsFilterProps {
  reportCards: newReportConfig[];
}

type DrillDownPathItem = { level: string; value: string };
type PathSummaryItem = { level: string; count: number; startIndex: number };

const summarizeDrillDownPath = (path: DrillDownPathItem[]) => {
  const summary: PathSummaryItem[] = [];
  path.forEach((item, index) => {
    const last = summary[summary.length - 1];
    if (last && last.level === item.level) {
      last.count += 1;
    } else {
      summary.push({ level: item.level, count: 1, startIndex: index });
    }
  });
  return summary;
};

const inferRootLevelsFromMap = (
  hierarchyMap?: Record<string, string[]>,
  explicitRoots?: string[]
): string[] => {
  if (!hierarchyMap || Object.keys(hierarchyMap).length === 0) {
    return [];
  }
  if (explicitRoots && explicitRoots.length > 0) {
    return explicitRoots;
  }

  const childLevels = new Set<string>();
  Object.values(hierarchyMap).forEach(children => {
    if (Array.isArray(children)) {
      children.forEach(child => childLevels.add(child));
    }
  });

  const inferredRoots = Object.keys(hierarchyMap).filter(level => !childLevels.has(level));
  return inferredRoots.length > 0 ? inferredRoots : Object.keys(hierarchyMap);
};

const getNextLevelsFromHierarchyMap = (
  hierarchyMap?: Record<string, string[]>,
  rootLevels?: string[],
  pathSummary: PathSummaryItem[] = []
): string[] => {
  if (!hierarchyMap || Object.keys(hierarchyMap).length === 0) {
    return [];
  }

  if (pathSummary.length === 0) {
    return inferRootLevelsFromMap(hierarchyMap, rootLevels);
  }

  const lastLevel = pathSummary[pathSummary.length - 1].level;
  const children = hierarchyMap[lastLevel] || [];
  return Array.from(new Set(children.filter(Boolean)));
};

const MdmReportsFilter: React.FC<MdmReportsFilterProps> = ({ reportCards }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const today = dayjs();
  const [fromDate, setFromDate] = useState<Dayjs | null>(today);
  const [toDate, setToDate] = useState<Dayjs | null>(today);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  const reportPropsFromState = (location.state as any)?.reportProps;
  const reportId = reportPropsFromState?.id; // Get reportId from navigation state
  const newReportConfigFromProps = reportPropsFromState?.newReportConfig;
  const dateRangeFilter = newReportConfigFromProps?.dateRangeFilter;
  const periodFilter = newReportConfigFromProps?.periodFilter;
  const isPDFReport = newReportConfigFromProps?.isPDFReport;
  
  const shouldShowDateRange = dateRangeFilter === true;
  const shouldShowPeriodFilter =  periodFilter === true;
  const shouldShowDistributorFilter = newReportConfigFromProps?.distributorFilter?.enabled;
  
  // Determine initial date range type based on configuration
  const getInitialDateRangeType = (): "daterange" | "period" => {
    if (shouldShowDateRange && !shouldShowPeriodFilter) return "daterange";
    if (!shouldShowDateRange && shouldShowPeriodFilter) return "period";
    // If both are enabled, default to daterange
    return "daterange";
  };
  
  const [dateRangeType, setDateRangeType] = useState<"daterange" | "period">(getInitialDateRangeType());
  
  const numberOfPeriods = useSelector(() => store.getState()?.fileFormat?.config?.numberOfPeriods) ?? 13;
  const currentYear = dayjs().year();
  const previousYear = currentYear - 1;
  const [period, setPeriod] = useState<string>("");
  const [year, setYear] = useState<string>("");
  
  const handleChangeYear = (event: SelectChangeEvent) => {
    setYear(event.target.value);
  };
  
  // Primary filter selection (Radio buttons)
  type PrimaryFilterType = "sales" | "geographical" | "distributor" | null;
  const [primaryFilter, setPrimaryFilter] = useState<PrimaryFilterType>(null);
  

  
  const selectedReport: newReportConfig | null = useMemo(() => {
    if (newReportConfigFromProps) return newReportConfigFromProps;
  
    if (reportId && reportCards?.length) {
      return reportCards.find(
        (card) => card.id === reportId || card.reportName === reportId
      ) ?? null;
    }
  
    return null;
  }, [reportId, reportCards, newReportConfigFromProps]);
  

  useEffect(() => {
    if (shouldShowDateRange && !shouldShowPeriodFilter) {
      setDateRangeType("daterange");
    } else if (!shouldShowDateRange && shouldShowPeriodFilter) {
      setDateRangeType("period");
    } else if (shouldShowDateRange && shouldShowPeriodFilter) {
      if (dateRangeType !== "daterange" && dateRangeType !== "period") {
        setDateRangeType("daterange");
      }
    }
  }, [shouldShowDateRange, shouldShowPeriodFilter]);
  
  // Dynamic filters state
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [optionsMap, setOptionsMap] = useState<Record<string, {label: string; value: string}[]>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [searchTextMap, setSearchTextMap] = useState<Record<string, string>>({});
  const GLOBAL_FILTERS = ["distributor_code"];
  
  // Custom filters state
  const [customFilters, setCustomFilters] = useState<FilterOption[]>([]);
  const [customFiltersLoading, setCustomFiltersLoading] = useState<boolean>(false);
  const [customFilterSelectionOrder, setCustomFilterSelectionOrder] = useState<string[]>([]);
  
  // Drill-down state for sales hierarchy
  const [salesDrillDownPath, setSalesDrillDownPath] = useState<Array<{level: string; value: string}>>([]);
  
  // Drill-down state for geographical hierarchy
  const [geoDrillDownPath, setGeoDrillDownPath] = useState<Array<{level: string; value: string}>>([]);
  
  const [geoHierarchyOrder, setGeoHierarchyOrder] = useState<string[]>([]);
  
  // Download state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const downloadCount = useSelector((state: any) => state.downloadBarReducer.downloadCount);
  const downloads = useSelector((state: any) => state.downloadBarReducer.downloads);
  const showDownload = useSelector((state: any) => state.downloadBarReducer.showDownload);

  const shouldShowFilters = !!selectedReport;
  const isDistributorView = selectedReport?.isDistributorView || false;

  const timeoutRefs = React.useRef<Record<string, NodeJS.Timeout>>({});

  const salesPathSummary = useMemo(
    () => summarizeDrillDownPath(salesDrillDownPath),
    [salesDrillDownPath]
  );

  const geoPathSummary = useMemo(
    () => summarizeDrillDownPath(geoDrillDownPath),
    [geoDrillDownPath]
  );

  const isGeoLastLevel = useMemo(() => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled || geoHierarchyOrder.length === 0) {
      return false;
    }
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (!currentLevel) return false;
    
    const lastLevel = geoHierarchyOrder[geoHierarchyOrder.length - 1];
    return currentLevel === lastLevel;
  }, [selectedReport?.geographicalHierarchyFilter, geoHierarchyOrder, filters]);


  // Load available custom filters when report is selected
  useEffect(() => {
    const loadCustomFiltersData = async () => {
      if (!selectedReport?.reportName) {
        setCustomFilters([]);
        setCustomFiltersLoading(false);
        return;
      }
      
      setCustomFiltersLoading(true);
      try {
        const filters = await loadCustomFiltersForReport(selectedReport);
        setCustomFilters(filters);
      } catch (error) {
        setCustomFilters([]);
      } finally {
        setCustomFiltersLoading(false);
      }
    };
    
    loadCustomFiltersData();
  }, [selectedReport?.reportName]);

  // Clear custom filter options when date range changes
  useEffect(() => {
    if (customFilters.length > 0) {
      // Clear options for all custom filters when dates change
      setOptionsMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      
      // Clear selections for custom filters
      setFilters(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      
      // Clear search texts
      setSearchTextMap(prev => {
        const updated = { ...prev };
        customFilters.forEach(filter => {
          delete updated[filter.alias];
        });
        return updated;
      });
      
      // Reset selection order
      setCustomFilterSelectionOrder([]);
    }
  }, [fromDate, toDate, customFilters]);

  // Helper function to get source alias from merged filter
  const getSourceAliasFromMergedFilter = useCallback((mergedFilterAlias: string, value: string): string | undefined => {
    const mergedSources = getMergedFilterSources(mergedFilterAlias, selectedReport);
    return mergedSources?.find(s => s.value === value)?.alias;
  }, [selectedReport]);

  // Check if a filter is a merged filter
  const isMergedFilter = useCallback((filterAlias: string): boolean => {
    return isMergedFilterForReport(filterAlias, selectedReport);
  }, [selectedReport]);

  // Build dependency filters for inter-dependent custom filters
  const buildMergedFilterDependencies = useCallback((excludeKey?: string, overrideFilters?: Record<string, string[]>): Record<string, string[]> => {
    const dependencyFilters: Record<string, string[]> = {};
    const sourceFilters = overrideFilters || filters;

    const allMergedFilterSourceAliases = new Set<string>();
    if (selectedReport?.mergedFilters) {
      Object.entries(selectedReport.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        sources.forEach(source => {
          allMergedFilterSourceAliases.add(source.alias);
        });
      });
    }

    const isExcludingMergedFilterSource = excludeKey && allMergedFilterSourceAliases.has(excludeKey);

    if (!isExcludingMergedFilterSource && selectedReport?.mergedFilters) {
      Object.entries(selectedReport.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        if (mergedFilterAlias === excludeKey) return;
        
        const mergedFilterSelections = sourceFilters[mergedFilterAlias] || [];
        if (mergedFilterSelections.length > 0) {
          const selectedValue = mergedFilterSelections[0];
          const sourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
          
          if (sourceAlias) {
            if (excludeKey === sourceAlias) {
              return;
            }
            const dynamicKey = `${mergedFilterAlias}_dynamic`;
            const dynamicSelections = sourceFilters[dynamicKey] || [];
            
            if (dynamicSelections.length > 0) {
              dependencyFilters[sourceAlias] = dynamicSelections;
            }
          }
        }
      });
    }

    customFilters.forEach(cf => {
      if (cf.alias === excludeKey) return;
      const vals = sourceFilters[cf.alias];
      if (vals && vals.length > 0 && vals[0] !== "") {
        dependencyFilters[cf.alias] = vals;
      }
    });

    return dependencyFilters;
  }, [filters, customFilters, selectedReport?.mergedFilters, getSourceAliasFromMergedFilter]);

  const loadFilterOptions = useCallback(async (key: string, q?: string, overrideFilters?: Record<string, string[]>) => {
    if (!selectedReport?.reportName) return;
    
    const effectiveFilters = overrideFilters || filters;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      const authContext = JSON.parse(localStorage.authContext || '{}');
      const loggedInUser = authContext?.user?.loginId;
      
      const additionalFilters: Record<string, string[]> | undefined = 
        selectedReport.isDistributorView && loggedInUser
          ? { distributor_code: [loggedInUser] }
          : undefined;

      const dependencyFilters = buildMergedFilterDependencies(key, effectiveFilters);

      let since: string | undefined;
      let until: string | undefined;
      
      if (fromDate && toDate) {
        const fromDateDayjs = dayjs(fromDate);
        const toDateDayjs = dayjs(toDate);
        
        since = new Date(Date.UTC(
          fromDateDayjs.year(),
          fromDateDayjs.month(),
          fromDateDayjs.date() - 1,
          18,
          30,
          0
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        
        until = new Date(Date.UTC(
          toDateDayjs.year(),
          toDateDayjs.month(),
          toDateDayjs.date(),
          18,
          29,
          59
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
      }

      if(selectedReport?.dateRangeFilter === false){
        since = undefined;
        until = undefined;
      }

      const values = await fetchFilterValues({
        report: selectedReport.reportName,
        which: key,
        contains: q?.trim(),
        page: 1,
        pageSize: 100,
        filters: Object.keys(dependencyFilters).length > 0 ? dependencyFilters : undefined,
        additionalFilters,
        since: since,
        until: until,
      });
      const filteredValues = values.filter(v => v && String(v).trim() !== '');
      const opts = filteredValues.map(v => ({ label: String(v), value: String(v) }));
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
    } catch (error) {
      console.error('[loadFilterOptions] Error loading options for:', key, error);
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [
    selectedReport?.reportName, 
    selectedReport?.isDistributorView,
    fromDate, 
    toDate, 
    buildMergedFilterDependencies,
    filters
  ]);

  const handleFilterOpen = (key: string) => {
    if (!optionsMap[key] || optionsMap[key].length === 0) {
      loadFilterOptions(key);
    }
  };

  const handleFilterChange = (key: string, value: string | null) => {
    setFilters(prev => {
      const newFilters = {
      ...prev,
      [key]: value ? [value] : []
      };
      
      const isGeographicalHierarchyValue = selectedReport?.geographicalHierarchyFilter?.enabled && 
                                           prev[selectedReport.geographicalHierarchyFilter.levelFilterField]?.[0] === key;
      
      if (isGeographicalHierarchyValue && selectedReport?.distributorFilter?.enabled) {
        delete newFilters[selectedReport.distributorFilter.field];
        
        setOptionsMap(prevOptions => {
          const updatedOptions = { ...prevOptions };
          delete updatedOptions[selectedReport.distributorFilter!.field];
          return updatedOptions;
        });
      }
      
      return newFilters;
    });
  };

  const handleMultiFilterChange = (key: string, values: string[]) => {
    const isCustomFilter = customFilters.some(cf => cf.alias === key);
    const hadSelections = filters[key] && filters[key].length > 0 && filters[key][0] !== "";
    const hasSelections = values.length > 0 && values[0] !== "";
    
    let updatedSelectionOrder = [...customFilterSelectionOrder];
    
    if (isCustomFilter) {
      if (!hasSelections && hadSelections) {
        updatedSelectionOrder = updatedSelectionOrder.filter(filterKey => filterKey !== key);
      } 
      else if (hasSelections && !hadSelections) {
        if (!updatedSelectionOrder.includes(key)) {
          updatedSelectionOrder = [...updatedSelectionOrder, key];
        }
      }
      else if (hasSelections && hadSelections) {
        const currentIndex = updatedSelectionOrder.indexOf(key);
        if (currentIndex === -1) {
          updatedSelectionOrder = [...updatedSelectionOrder, key];
        }
      }
    }
    
    const filtersToClear: string[] = [];
    if (isCustomFilter && hasSelections) {
      const currentIndex = customFilterSelectionOrder.indexOf(key);
      if (currentIndex !== -1) {
        filtersToClear.push(...customFilterSelectionOrder.slice(currentIndex + 1));
      }
    } else if (isCustomFilter && !hasSelections && hadSelections) {
      const currentIndex = customFilterSelectionOrder.indexOf(key);
      if (currentIndex !== -1) {
        filtersToClear.push(...customFilterSelectionOrder.slice(currentIndex + 1));
      }
    }
    
    setCustomFilterSelectionOrder(updatedSelectionOrder);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: values
      };
      
      if (filtersToClear.length > 0) {
        filtersToClear.forEach(dependentKey => {
          if (newFilters[dependentKey]) {
            delete newFilters[dependentKey];
          }
        });
      }
      
      return newFilters;
    });

    // Invalidate cached options for dependent custom filters
    if (isCustomFilter) {
      const nextFilters = { ...filters, [key]: values };
      const filtersToInvalidate = customFilters
        .map(cf => cf.alias)
        .filter(alias => alias !== key)
        .filter(alias => {
          const vals = nextFilters[alias];
          return !vals || vals.length === 0 || vals[0] === "";
        });

      if (filtersToInvalidate.length > 0) {
        setFilters(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            if (updated[alias] && updated[alias].length > 0) {
              delete updated[alias];
            }
          });
          return updated;
        });
        setOptionsMap(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            delete updated[alias];
          });
          return updated;
        });

        setSearchTextMap(prev => {
          const updated = { ...prev };
          filtersToInvalidate.forEach(alias => {
            delete updated[alias];
          });
          return updated;
        });
      }
    }
    
    if (filtersToClear.length > 0) {
      setOptionsMap(prevOptions => {
        const updatedOptions = { ...prevOptions };
        filtersToClear.forEach(dependentKey => {
          delete updatedOptions[dependentKey];
        });
        return updatedOptions;
      });
      
      setSearchTextMap(prevSearch => {
        const updatedSearch = { ...prevSearch };
        filtersToClear.forEach(dependentKey => {
          delete updatedSearch[dependentKey];
        });
        return updatedSearch;
      });
    }
  };

  const handleFilterInputChange = (key: string, inputValue: string) => {
    // Update the search text immediately for UI responsiveness
    setSearchTextMap(prev => ({ ...prev, [key]: inputValue }));
    
    // Clear any existing timeout for this filter
    if (timeoutRefs.current[key]) {
      clearTimeout(timeoutRefs.current[key]);
    }
    timeoutRefs.current[key] = setTimeout(() => {
      loadFilterOptions(key, inputValue);
    }, 350);
  };


  

  const loadSalesValues = useCallback(async (designation: string) => {
    if (!selectedReport?.salesHierarchyFilter?.enabled) return;
    
    const key = designation;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      let users: string[] = [];
      

      if (salesDrillDownPath.length === 0) {
        users = await fetchUsersByDesignation(designation);
      } else {

        const lastLevelUsers = salesDrillDownPath
          .filter(item => item.level === salesDrillDownPath[salesDrillDownPath.length - 1].level)
          .map(item => item.value);
        

        const allChildrenPromises = lastLevelUsers.map(userId => 
          fetchChildrenUsers(userId, designation)
        );
        
        const allChildrenResults = await Promise.all(allChildrenPromises);

        const allChildren = allChildrenResults.flat();
        users = Array.from(new Set(allChildren));
      }
      
      const opts = users.map(u => ({ label: u, value: u }));
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [selectedReport?.salesHierarchyFilter, salesDrillDownPath]);

  // Load geographical hierarchy levels
  const loadGeographicalLevels = useCallback(async () => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled) return;

    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    setLoadingMap(prev => ({ ...prev, [levelKey]: true }));
    try {
      let availableLevels: string[] = [];
      

      const levelsFromApi = await fetchGeographicalLevels();
      const allLevels = levelsFromApi.reverse();
      
      // Store the hierarchy order for later use
      setGeoHierarchyOrder(allLevels);
      
      if (geoPathSummary.length > 0) {
        const lastSelectedLevel = geoPathSummary[geoPathSummary.length - 1].level;
        const lastSelectedIndex = allLevels.indexOf(lastSelectedLevel);
        
        if (lastSelectedIndex !== -1 && lastSelectedIndex < allLevels.length - 1) {
          availableLevels = [allLevels[lastSelectedIndex + 1]];
        } else {
          availableLevels = [];
        }
      } else {
        availableLevels = allLevels;
      }
      
      const opts = availableLevels.map(l => ({ label: l, value: l }));
      setOptionsMap(prev => ({ ...prev, [levelKey]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [levelKey]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [levelKey]: false }));
    }
  }, [selectedReport?.geographicalHierarchyFilter, geoPathSummary]);

  const loadGeographicalValues = useCallback(async (level: string) => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled) return;

    const key = level;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      let locations: string[] = [];
      
      const levelsFromApi = await fetchGeographicalLevels();
      const hierarchyOrder = levelsFromApi.reverse();
      

      let isFreshSelection = geoDrillDownPath.length === 0;
      
      if (!isFreshSelection && hierarchyOrder.length > 0) {
        const lastPathLevel = geoDrillDownPath[geoDrillDownPath.length - 1].level;
        const lastPathLevelIndex = hierarchyOrder.indexOf(lastPathLevel);
        const selectedLevelIndex = hierarchyOrder.indexOf(level);
        
        if (lastPathLevelIndex === -1 || selectedLevelIndex === -1 || 
            selectedLevelIndex !== lastPathLevelIndex + 1) {
          isFreshSelection = true;
        }
      }
      
      if (isFreshSelection) {
        // First selection or selection after reset: fetch all locations at this level
        locations = await fetchGeographicalLocations(level);
      } else {

        const lastLevelLocations = geoDrillDownPath
          .filter(item => item.level === geoDrillDownPath[geoDrillDownPath.length - 1].level)
          .map(item => item.value);
        
        const lastLevel = geoDrillDownPath[geoDrillDownPath.length - 1].level;
        
        const allChildrenPromises = lastLevelLocations.map(locationValue => {
          return fetchGeographicalLocationsUnder(lastLevel, locationValue, level);
        });
        
        const allChildrenResults = await Promise.all(allChildrenPromises);
        
        const allChildren = allChildrenResults.flat();
        locations = Array.from(new Set(allChildren));
      }
      
      const opts = locations.map(l => ({ label: l, value: l }));
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [selectedReport?.geographicalHierarchyFilter, geoDrillDownPath]);


  const hasGeographicalSelection = (): boolean => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled) return false;

    if (geoDrillDownPath && geoDrillDownPath.length > 0) return true;
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    if (currentLevel) {
      const values = filters[currentLevel] || [];
      if (values.length > 0) return true;
    }

    return false;
  };

  const shouldDisableDistributorFilter = useMemo(() => {
    if (primaryFilter !== "geographical") return false;
    return !hasGeographicalSelection();
  }, [
    primaryFilter,
    filters,
    geoDrillDownPath,
    selectedReport?.geographicalHierarchyFilter?.enabled,
    selectedReport?.geographicalHierarchyFilter?.levelFilterField
  ]);


    const handleClose = () => {
    navigate(-1);
  };

  const handlePreview = async () => {
    if (!selectedReport) {
      return;
    }

    if (fromDate && toDate && fromDate.isAfter(toDate)) {
      openPopup("Error", "To Date should be greater than from date.");
      return;
    }

    // Validate that at least one distributor is selected
    const distributorCodes = await getDistributorCodesForPreview();
    if (distributorCodes.length === 0) {
      openPopup("Error", "Please select at least one distributor");
      return;
    }

    const reportFilters: any = {
      dateRange: {
        type: dateRangeType,
        fromDate: (fromDate || today).format("DD-MM-YYYY"),
        toDate: (toDate || today).format("DD-MM-YYYY"),
        selectedRange: "custom"
      },
      filters: filters,
      reportName: selectedReport.reportName
    };
    
    // Add period and year if period filter is selected
    if (dateRangeType === "period") {
      reportFilters.period = period;
      reportFilters.year = year;
    }
    setShowPreview(true);
  };

  // Helper function to collect distributor codes for preview validation
  const getDistributorCodesForPreview = async (): Promise<string[]> => {
    if (!selectedReport) return [];
    
    return await collectDistributorCodes(
      selectedReport,
      filters,
      primaryFilter,
    );
  };

  const handleBackToFilters = () => {
    setShowPreview(false);
  };

  // Download handlers
  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (format: string) => {
    if (!selectedReport?.reportName) {
      handleDownloadClose();
      return;
    }

    if (fromDate && toDate && fromDate.isAfter(toDate)) {
      openPopup("Error", "To Date should be greater than from date.");
      return;
    }

    // Check if any distributors are available before downloading
    const distributorCodes = await collectDistributorCodes(
      selectedReport,
      filters,
      primaryFilter,
    );

    if (distributorCodes.length === 0) {
      openPopup("Error", "Please select at least one distributor");
      handleDownloadClose();
      return;
    }

    handleDownloadClose();
    setDownloadLoader(true);

    try {
      const result = await handleMdmReportDownload({
        selectedReport,
        filters,
        dateRangeType,
        fromDate: fromDate || today,
        toDate: toDate || today,
        period,
        year,
        format,
        primaryFilter,
        customFilters: customFilters.map(f => f.alias),
      });

      setDownloadLoader(false);

      if (result.success) {
        openPopup("Success", result.message);
      } else {
        openPopup("Error", result.message);
      }

    } catch (error: any) {
      setDownloadLoader(false);
      const errorMessage = error.message || "Failed to download report. Please try again.";
      openPopup("Error", errorMessage);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Memoize dateRange to prevent unnecessary preview API calls
  const memoizedDateRange = useMemo(() => ({
    type: dateRangeType,
    fromDate: (fromDate || today).format("DD-MM-YYYY"),
    toDate: (toDate || today).format("DD-MM-YYYY"),
    selectedRange: "custom",
    period: dateRangeType === "period" ? period : undefined,
    year: dateRangeType === "period" ? year : undefined
  }), [dateRangeType, fromDate, toDate, period, year, today]);

  // Memoize customFilters to prevent unnecessary preview API calls
  const memoizedCustomFilters = useMemo(() => customFilters, [customFilters]);

  return (
    <div className="mdm-reports-page">
      {/* Header with Navbar */}
      <div className="mdm-reports-header">
        <div className="mdm-reports-header-left">
          <ArrowBack 
            className="mdm-reports-back-icon" 
            onClick={handleClose}
            sx={{
              fontSize: '18px',
              cursor: 'pointer'
            }}
          />
          <Typography variant="h6" className="mdm-reports-title">
            {reportPropsFromState?.name || selectedReport?.name || 'Report'}
          </Typography>
        </div>
        <Close 
          className="mdm-reports-close-icon" 
          onClick={handleClose}
          sx={{
            fontSize: '26px',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Main Content Container */}
      <div className="mdm-reports-container">
        {/* Left Section */}
        <div className="mdm-reports-left-section">
          {showPreview ? (
            <MdmReportsPreview 
              onBack={handleBackToFilters} 
              newReportConfig={selectedReport}
              filters={filters}
              dateRange={memoizedDateRange}
              customFilters={memoizedCustomFilters}
            />
          ) : (
            <div className="mdm-reports-left-content">
              <h5>
                {selectedReport?.isLiveReport 
                  ? "Live reports cannot be previewed. Kindly download the report to continue."
                  : selectedReport?.isPDFReport
                  ? "PDF reports cannot be previewed. Kindly download the report to continue."
                  : "Select filters to generate report"}
              </h5>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="mdm-reports-right-section">
        <div className="mdm-reports-scrollable">
        {(shouldShowDateRange || shouldShowPeriodFilter) && (
          <>
        {/* Header */}
        <div className="mdm-reports-filter-header">
              <h3>
                {dateRangeType === "daterange" 
                  ? "Choose the date range in which you want to fetch your data"
                  : "Choose the period in which you want to fetch your data"}
              </h3>
        </div>

            {/* Date Range Type Selection - Show radio buttons only if both filters are enabled */}


            {/* Date Range Filter - Using NewDateFilter component */}
            {dateRangeType === "daterange" && shouldShowDateRange && (
              <>
                <div style={{ padding: '8px 0 4px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#333', minWidth: '100px' }}>
                    Date Range <span style={{ color: 'red', fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>*</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <DateRangeSelector
                      fromDate={fromDate}
                      toDate={toDate}
                      setFromDate={setFromDate}
                      setToDate={setToDate}
                      reportConfig={selectedReport}
                      isDistributorView={isDistributorView}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Period Filter - Show when period is selected and periodFilter is enabled */}

          </>
        )}

        {/* Dynamic Custom Filters from API */}
        {selectedReport?.shouldShowCustomFilters && (
          <div className="mdm-reports-filter-section" style={{ marginTop: '8px' }}>
            <div className="mdm-reports-filter-section-title">Custom Report Filters</div>
            {customFiltersLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0' }}>
                <CircularProgress size={20} />
                <span style={{ fontSize: '14px', color: '#666' }}>Loading filters...</span>
              </div>
            ) : customFilters.length > 0 ? (
              <>
            {customFilters.map((filter: FilterOption) => {
              // Using alias as key to store/retrieve data
              const filterKey = filter.alias;
              const currentValues = filters[filterKey] || [];
              const options = optionsMap[filterKey] || [];
              const isLoading = loadingMap[filterKey] || false;
              const optionValues = new Set(options.map(opt => opt.value));
              const missingSelected = currentValues
                .filter(val => !optionValues.has(val))
                .map(val => ({ label: val, value: val }));
              const optionsWithSelected = [...options, ...missingSelected];

              // Custom renderTags to show first value + count
              const renderTags = (tagValue: { label: string; value: string }[], getTagProps: any) => {
                if (tagValue.length === 0) return null;
                
                const firstTag = tagValue[0];
                const remainingCount = tagValue.length - 1;
                
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    <Chip
                      {...getTagProps({ index: 0 })}
                      label={firstTag.label}
                      size="small"
                      style={{ maxWidth: '200px' }}
                    />
                    {remainingCount > 0 && (
                      <Chip
                        label={`+${remainingCount}`}
                        size="small"
                        style={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </div>
                );
              };

              const searchText = searchTextMap[filterKey] || '';

              return (
                <div key={filterKey} className="mdm-reports-filter-item" style={{ marginBottom: '12px' }}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    disablePortal
                    inputValue={searchText}
                    slotProps={{ 
                      popper: { 
                        placement: 'bottom-start', 
                        modifiers: [{ name: 'flip', enabled: false }],
                        style: { zIndex: 1300 }
                      },
                      paper: {
                        style: {
                          maxHeight: 300,
                          overflow: 'auto'
                        }
                      }
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: 300,
                        overflow: 'auto'
                      }
                    }}
                    options={optionsWithSelected}
                    value={optionsWithSelected.filter(opt => currentValues.includes(opt.value))}
                    onChange={(event, newValues) => {
                      const selectedValues = newValues.map(v => v.value);
                      handleMultiFilterChange(filterKey, selectedValues);
                    }}
                    onOpen={() => handleFilterOpen(filterKey)}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        handleFilterInputChange(filterKey, newInputValue);
                      }
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    loading={isLoading}
                    fullWidth
                    renderTags={renderTags}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={filter.display}
                        placeholder={`Select ${filter.display}`}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>
              );
            })}
              </>
            ) : (
              <div style={{ padding: '12px 0', fontSize: '14px', color: '#999' }}>
                No custom filters available for this report
              </div>
            )}
          </div>
        )}
        </div>

        {/* Sticky footer: buttons */}
        <div className="mdm-reports-note">
          <div className="mdm-reports-footer-buttons">
            {
            selectedReport?.isLiveReport !== true && selectedReport?.isPDFReport !== true &&  (
            <Button
              variant="contained"
              className="mdm-reports-preview-button"
              onClick={handlePreview}
            >
              Preview
            </Button>
)}
            {downloadLoader ? (
              <div className="mdm-reports-download-loader">
                <Loader />
              </div>
            ) : (
              <>
                <Button
                  variant="contained"
                  className="mdm-reports-download-button"
                  startIcon={<GetApp />}
                  endIcon={<KeyboardArrowDown />}
                  onClick={handleDownloadClick}
                >
                  Download
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleDownloadClose}
                  className="mdm-reports-download-menu"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                >
                  {isPDFReport === true ? (
                    <MenuItem onClick={() => handleDownload('pdf')}>
                      <span className="mdm-reports-menu-item">PDF</span>
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem onClick={() => handleDownload('csv')}>
                        <span className="mdm-reports-menu-item">CSV</span>
                      </MenuItem>
                      <MenuItem onClick={() => handleDownload('xlsx')}>
                        <span className="mdm-reports-menu-item">XLS</span>
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MdmReportsFilter;